(ns ui.gallery.folder-tree
  "The folder tree of the Replicant gallery: the chain of parent folders,
   the list of subfolders of the current folder, the drop zones of the
   drag & drop and the right-click context menu of a folder. The
   components take the gallery state map (the `:gallery` entry of the
   store) and the `:folder-tree` of the gallery query result."
  (:require [clojure.string :as str]
            [storrito.ui.button :as button]
            [ui.elements]
            [ui.gallery.drag :as drag]
            [ui.gallery.layout :as layout]))

(def folder-svg
  [:svg {:xmlns "http://www.w3.org/2000/svg" :class ["h-6" "w-6"] :fill "none"
         :viewBox "0 0 24 24" :stroke "currentColor" :stroke-width "2"}
   [:path {:stroke-linecap "round" :stroke-linejoin "round"
           :d "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"}]])

(def add-new-folder-svg
  [:svg {:xmlns "http://www.w3.org/2000/svg" :class ["h-6" "w-6"] :fill "none"
         :viewBox "0 0 24 24" :stroke "currentColor" :stroke-width "2"}
   [:path {:stroke-linecap "round" :stroke-linejoin "round"
           :d "M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"}]])

(def dots-horizontal-svg
  [:svg {:xmlns "http://www.w3.org/2000/svg" :class ["h-5" "w-5"] :fill "currentColor"
         :viewBox "0 0 20 20"}
   [:path {:d "M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z"}]])

(defn drop-target-on
  "The event handlers that turn an element into a drop target for
   gallery drags (media entries or folders) on the folder `uuid`. The
   `opts` map with `:current-folder? true` marks the drop zones of the
   current folder, which never spring open and only accept drags that
   started in another folder. The handlers check the validity of the
   drop, see `ui.gallery.actions`."
  [uuid opts]
  {:dragenter [[:gallery/drag-enter uuid opts]]
   :dragover [[:gallery/drag-over uuid opts]]
   :dragleave [[:gallery/drag-leave uuid]]
   :drop [[:gallery/drop uuid opts]]})

(defn drop-target-class
  "Visual feedback classes for a folder drop target: a subtle outline on
   every valid target while a drag is in flight, plus a strong highlight
   on the currently hovered target."
  [drag uuid]
  (when (drag/valid-drop-target? drag uuid)
    (if (drag/over? drag uuid)
      ["bg-blue-100" "ring-2" "ring-blue-400" "ring-inset" "rounded-md"]
      ["ring-1" "ring-blue-200" "ring-inset" "rounded-md"])))

(defn current-folder-drop-class
  "Visual feedback classes for a current-folder drop target: a dashed
   outline while a drag from another folder is in flight, plus a strong
   highlight while the drag hovers over the target."
  [drag uuid]
  (when (drag/valid-current-folder-target? drag uuid)
    (if (drag/over? drag uuid)
      ["bg-blue-100" "border-2" "border-dashed" "border-blue-400" "rounded-md"]
      ["border-2" "border-dashed" "border-blue-200" "rounded-md"])))

(defn spring-load-indicator
  "A translucent fill that sweeps from left to right over the hovered
   folder drop target `uuid` during the spring-load delay, so the user
   sees that holding the drag there is about to open the folder. The
   host element needs `relative` positioning."
  [drag uuid]
  (when (and (drag/valid-drop-target? drag uuid)
             (drag/over? drag uuid))
    [:span.absolute.inset-0.rounded-md.origin-left.pointer-events-none
     {:class ["bg-blue-400/40"]
      :style {:animation (str "spring-load "
                              drag/spring-load-delay-ms
                              "ms linear forwards")}}]))

(defn menu-button
  "The '...' button that appears when hovering a folder row (the row has
   the Tailwind `group` class) and opens the same menu as a right-click
   on the row. Hidden while a drag is in flight, so it never sits under
   the cursor during a drop."
  [drag uuid name]
  [:button.shrink-0.ml-1.p-1.rounded.text-gray-500.hover:bg-gray-300
   {:type "button"
    :title "Folder actions"
    ;; Visibility is controlled via opacity only: the mouse can only be
    ;; on the invisible button while the row (its Tailwind `group`) is
    ;; hovered, which makes the button visible — so it never steals a
    ;; click while invisible.
    :class (if (drag/active? drag)
             ["opacity-0" "pointer-events-none"]
             ["opacity-0" "group-hover:opacity-100" "focus:opacity-100"])
    ;; Do not open the folder (stop-propagation) when the menu opens:
    :on {:click [[:js/stop-propagation]
                 [:gallery/context-menu uuid name]]}}
   dots-horizontal-svg])

(defn current-folder-drop-zone
  "The empty space below the folder list. While a drag from another
   folder is in flight, it offers to move the dragged entries into the
   current folder. Otherwise it only tells that the folder has no
   subfolders (when it has none)."
  [gallery folders current-folder subfolders?]
  (let [drag (:drag gallery)
        target? (drag/valid-current-folder-target? drag current-folder)]
    [:div.flex.grow.justify-center.items-center.text-center.text-sm.text-gray-500.p-2.m-1
     {:class (concat (current-folder-drop-class drag current-folder)
                     ;; keeps the zone reachable when the folder list
                     ;; fills the panel:
                     (when target?
                       ["min-h-16"]))
      :on (drop-target-on current-folder {:current-folder? true})}
     (cond
       target?
       (str "Drop here to move into \""
            (layout/current-folder-name folders current-folder)
            "\"")

       (not subfolders?)
       "This folder has no subfolders.")]))

(defn chain-entry
  [gallery folders current-folder entry]
  (let [drag (:drag gallery)]
    (if-let [hidden (:ellipsis entry)]
      [:span.whitespace-nowrap.cursor-default
       {:title (str/join " / "
                         (map (fn [uuid]
                                (get-in folders [uuid :name]))
                              hidden))}
       "…"]
      (let [name (get-in folders [entry :name])]
        (if (= entry current-folder)
          [:span.flex-1.overflow-hidden.text-ellipsis.whitespace-nowrap.max-w-fit
           (layout/current-folder-name folders entry)]
          [:a.relative.underline.cursor-pointer.flex-1.overflow-hidden.text-ellipsis.whitespace-nowrap.max-w-fit
           {:class (drop-target-class drag entry)
            :on (merge {:click [[:gallery/open-folder entry]]}
                       (drop-target-on entry {}))}
           name
           (spring-load-indicator drag entry)])))))

(defn folder-chain
  "The chain of parent folders down to the current folder, each parent a
   link and a drop target, plus the 'new folder' button. The optional
   `component` is rendered after the chain (the mobile popover puts its
   toggle there). Without a folder tree (no folders yet) the chain
   consists of the 'Gallery' root only."
  ([gallery folder-tree]
   (folder-chain gallery folder-tree nil))
  ([gallery {:keys [folders current-folder] :as folder-tree} component]
   [:div.flex.bg-white.rounded-sm.shadow
    [:div.w-full.overflow-hidden.p-2.flex.items-center
     (map (fn [entry]
            [:span.flex.items-center.min-w-0
             {:replicant/key (or (:ellipsis entry) entry ::root)}
             (chain-entry gallery folders current-folder entry)
             [:span "/"]])
          (layout/collapse-chain (layout/display-chain folder-tree)))
     component]
    [:div.p-1.cursor-pointer.border.rounded.hover:bg-gray-100.flex.items-center
     {:title "Create new folder"
      :on {:click [[:gallery/folder-new current-folder]]}}
     add-new-folder-svg]]))

(defn folder-row
  "A subfolder of the current folder: a click opens it, it can be dragged
   into another folder and is a drop target itself."
  [gallery current-folder [uuid {:keys [name]}]]
  (let [drag (:drag gallery)]
    [:div.group.relative.flex.w-full.items-center.p-2.hover:bg-gray-200.cursor-pointer
     {:replicant/key uuid
      :class (concat ["transition-opacity" "duration-300"]
                     (when (drag/dragged-folder? drag uuid)
                       ["opacity-50"])
                     (when (drag/moving-out? gallery uuid)
                       ["opacity-0" "pointer-events-none"])
                     (drop-target-class drag uuid))
      :draggable true
      :on (merge {:click [[:gallery/open-folder uuid]]
                  :contextmenu [[:gallery/context-menu uuid name]]
                  :dragstart [[:gallery/drag-start-folder uuid name current-folder]]
                  :dragend [[:gallery/drag-end]]}
                 (drop-target-on uuid {}))}
     [:div.mr-2 folder-svg]
     [:span.grow.min-w-0.overflow-hidden.text-ellipsis.whitespace-nowrap
      {:title name}
      name]
     (menu-button drag uuid name)
     (spring-load-indicator drag uuid)]))

(defn folder-list
  "The subfolders of the current folder and the drop zone below them."
  [gallery {:keys [folders current-folder]}]
  (let [subfolders (layout/sorted-subfolders folders current-folder)]
    [:div.overflow-y-auto.grow.flex.flex-col
     (map (partial folder-row gallery current-folder)
          subfolders)
     (current-folder-drop-zone gallery
                               folders
                               current-folder
                               (boolean (seq subfolders)))]))

(defn move-folder-list
  "The subfolders of the current folder inside the 'Move' popover of the
   selection bar, plus the 'Move here' button that moves the selected
   entries into the current folder."
  [_gallery {:keys [folders current-folder]}]
  (let [subfolders (layout/sorted-subfolders folders current-folder)]
    [:div.grow.flex.flex-col
     [:div.overflow-y-auto.grow.flex.flex-col
      {:style {:max-height "20vw"
               :min-height "3rem"}}
      (when-not (seq subfolders)
        [:div.flex.grow.min-h-0.justify-center.items-center
         "This folder has no subfolders."])
      (map (fn [[uuid {:keys [name]}]]
             [:div.flex.w-full.items-center.p-2.cursor-pointer.hover:bg-gray-200
              {:replicant/key uuid
               :on {:click [[:gallery/open-folder uuid]]
                    :contextmenu [[:gallery/context-menu uuid name]]}}
              [:div.mr-2 folder-svg]
              [:a.overflow-hidden.text-ellipsis.whitespace-nowrap
               {:title name}
               name]])
           subfolders)]
     [:button
      {:class (concat button/button-primary-css ["mr-2"])
       :type "button"
       :title "Moves the selected items into the current folder."
       :on {:click [[:gallery/move-here]]}}
      "Move here"]]))

(defn panel
  "The folder tree panel left of the grid."
  [gallery folder-tree]
  [:div.flex.flex-col.overflow-hidden.h-full
   {:class (concat ui.elements/panel ["!p-0"])
    :style {:width "250px"}}
   (folder-chain gallery folder-tree)
   [:hr]
   (folder-list gallery folder-tree)])

(defn current-folder-drop-zone-wrapper
  "Wraps the gallery grid (or the empty card) into a drop target for the
   current folder, so entries can be dropped into the folder that sprang
   open mid-drag without aiming at the folder tree."
  [gallery current-folder child]
  [:div.flex.flex-col.grow.min-h-0
   {:class (current-folder-drop-class (:drag gallery) current-folder)
    :on (drop-target-on current-folder {:current-folder? true})}
   child])

(def context-menu-entries
  [{:label "Rename"
    :action :gallery/folder-rename-ask}
   {:label "Delete"
    :action :gallery/folder-delete-ask}])

(defn context-menu
  "The right-click menu of a folder, positioned at the click. The
   transparent backdrop closes it on the next click anywhere."
  [gallery]
  (when-let [{:keys [x y uuid name]} (:context-menu gallery)]
    [:div
     [:div.fixed.inset-0.z-40
      {:on {:click [[:gallery/close-context-menu]]
            :contextmenu [[:js/prevent-default]
                          [:gallery/close-context-menu]]}}]
     [:div.fixed.z-50
      {:style {:top (str y "px")
               :left (str x "px")}}
      [:div.bg-white.w-40.border.border-gray-300.rounded-lg.flex.flex-col.text-sm.py-2.px-2.text-gray-500.shadow-lg
       (map (fn [{:keys [label action]}]
              [:div.flex.hover:bg-gray-100.py-1.px-2.rounded.cursor-pointer
               {:replicant/key label
                :on {:click [[:gallery/close-context-menu]
                             [action uuid name]]}}
               label])
            context-menu-entries)]]]))
